/**
 * Utility functions for the Admin Dashboard
 */

import { type ClassValue, clsx } from 'clsx';

/**
 * Merge class names with Tailwind CSS support
 */
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}

/**
 * Helper to check if a date is valid
 */
function isValidDate(d: Date): boolean {
  return d instanceof Date && !isNaN(d.getTime());
}

/**
 * Format date to German locale
 */
export function formatDate(
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!isValidDate(d)) return '-';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  });
}

/**
 * Format date and time to German locale
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!isValidDate(d)) return '-';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format time only
 */
export function formatTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!isValidDate(d)) return '-';
  return d.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time (e.g., "vor 5 Minuten")
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (!isValidDate(d)) return '-';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Minute${diffMins !== 1 ? 'n' : ''}`;
  if (diffHours < 24) return `vor ${diffHours} Stunde${diffHours !== 1 ? 'n' : ''}`;
  if (diffDays < 7) return `vor ${diffDays} Tag${diffDays !== 1 ? 'en' : ''}`;
  return formatDate(d);
}

/**
 * Format minutes to hours and minutes
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} Std.`;
  return `${hours} Std. ${mins} Min.`;
}

/**
 * Format number to German locale
 */
export function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format currency to EUR
 */
export function formatCurrency(value: number): string {
  return value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
}

/**
 * Format percentage
 */
export function formatPercent(value: number, decimals = 0): string {
  return `${formatNumber(value, decimals)}%`;
}

/**
 * Truncate string
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

/**
 * Get initials from name
 */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Generate a color for a status
 */
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    // Vehicle statuses (uppercase from Prisma)
    ANNOUNCED: 'bg-gray-100 text-gray-800',
    INCOMING: 'bg-yellow-100 text-yellow-800',
    ON_YARD: 'bg-blue-100 text-blue-800',
    IN_PROCESS: 'bg-purple-100 text-purple-800',
    QC_PENDING: 'bg-orange-100 text-orange-800',
    READY: 'bg-green-100 text-green-800',
    OUTGOING: 'bg-cyan-100 text-cyan-800',
    DEPARTED: 'bg-gray-100 text-gray-800',
    // Task statuses
    PENDING: 'bg-yellow-100 text-yellow-800',
    ASSIGNED: 'bg-blue-100 text-blue-800',
    // IN_PROGRESS already covered above
    ON_HOLD: 'bg-orange-100 text-orange-800',
    COMPLETED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    FAILED: 'bg-red-100 text-red-800',
    // Workflow statuses
    active: 'bg-green-100 text-green-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
    failed: 'bg-red-100 text-red-800',
    paused: 'bg-orange-100 text-orange-800',
    // SLA statuses
    on_track: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    overdue: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Get priority color
 */
export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    LOW: 'bg-gray-100 text-gray-600',
    NORMAL: 'bg-blue-100 text-blue-600',
    HIGH: 'bg-orange-100 text-orange-600',
    URGENT: 'bg-red-100 text-red-600',
  };
  return colors[priority] || 'bg-gray-100 text-gray-600';
}

/**
 * Get task type label in German
 */
export function getTaskTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    CHECKIN: 'Check-In',
    DAMAGE_ASSESSMENT: 'Schadenaufnahme',
    KVA_CREATION: 'KVA erstellen',
    REPAIR: 'Reparatur',
    CLEANING: 'Reinigung',
    QC_INSPECTION: 'QS-Prufung',
    FINAL_CHECK: 'Endkontrolle',
    HANDOVER: 'Ubergabe',
    MOVE: 'Umparken',
    CUSTOM: 'Sonstige',
  };
  return labels[type] || type;
}

/**
 * Get vehicle status label in German
 */
export function getVehicleStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    ANNOUNCED: 'Angekündigt',
    INCOMING: 'Eingehend',
    ON_YARD: 'Auf dem Hof',
    IN_PROCESS: 'In Bearbeitung',
    QC_PENDING: 'QS ausstehend',
    READY: 'Bereit',
    OUTGOING: 'Ausgehend',
    DEPARTED: 'Abgefahren',
  };
  return labels[status] || status;
}

/**
 * Get role label in German
 */
export function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    SUPER_ADMIN: 'Super Admin',
    ADMIN: 'Administrator',
    DISPATCHER: 'Disponent',
    YARD_WORKER: 'Hofarbeiter',
    WORKSHOP: 'Werkstatt',
    CLEANING: 'Reinigung',
    QC_INSPECTOR: 'QS-Prufer',
    CUSTOMER_ADMIN: 'Kunde Admin',
    CUSTOMER_USER: 'Kunde Benutzer',
    CUSTOMER_READONLY: 'Kunde (nur lesen)',
  };
  return labels[role] || role;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Download data as file
 */
export function downloadFile(data: Blob, filename: string): void {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export data to CSV
 */
export function exportToCSV<T extends object>(
  data: T[],
  headers: { key: keyof T; label: string }[],
  filename: string,
): void {
  const csvHeaders = headers.map((h) => h.label).join(';');
  const csvRows = data.map((row) =>
    headers
      .map((h) => {
        const value = row[h.key];
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && value.includes(';')) {
          return `"${value}"`;
        }
        return String(value);
      })
      .join(';'),
  );
  const csv = [csvHeaders, ...csvRows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  downloadFile(blob, `${filename}.csv`);
}

/**
 * Parse query params from URL
 */
export function parseQueryParams<T extends Record<string, string>>(
  searchParams: URLSearchParams,
): Partial<T> {
  const params: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params as Partial<T>;
}

/**
 * Build query string from object
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.set(key, String(value));
      }
    }
  });
  return searchParams.toString();
}

/**
 * Extract paginated data from API response
 * API returns ApiResponse<T> = { success, data: { items, meta }, timestamp }
 * This extracts the inner { items, meta } object
 */
export function extractPaginatedData<T>(
  response:
    | {
        data?: {
          items: T[];
          meta: { total: number; page?: number; limit?: number; totalPages?: number };
        };
      }
    | undefined,
):
  | { items: T[]; meta: { total: number; page?: number; limit?: number; totalPages?: number } }
  | undefined {
  return response?.data;
}

/**
 * Extract items array from paginated API response
 */
export function extractItems<T>(response: { data?: { items: T[] } } | undefined): T[] {
  return response?.data?.items || [];
}
